'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useEnvironment } from '@/contexts/useEnvironment';
import { useUser } from '@/contexts/useUser';

export interface ToolCallInfo {
  toolCallId: string;
  toolName: string;
  status: 'calling' | 'done';
}

interface SSEEvent {
  type: string;
  [key: string]: unknown;
}

interface UseSparkJobExplainReturn {
  markdown: string;
  isStreaming: boolean;
  isComplete: boolean;
  error: string | null;
  toolCalls: ToolCallInfo[];
  explain: () => void;
  reset: () => void;
}

export function useSparkJobExplain(
  workspaceId: string,
  sparkJobId: string
): UseSparkJobExplainReturn {
  const { clientApiBaseUrl, authToken } = useEnvironment();
  const { currentProjectId } = useUser();

  const [markdown, setMarkdown] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toolCalls, setToolCalls] = useState<ToolCallInfo[]>([]);

  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setMarkdown('');
    setIsStreaming(false);
    setIsComplete(false);
    setError(null);
    setToolCalls([]);
  }, []);

  const explain = useCallback(() => {
    // Prevent duplicate calls
    if (isStreaming) return;

    reset();
    setIsStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    const url = `${clientApiBaseUrl}/projects/${currentProjectId}/lakehouse/workspace/${workspaceId}/spark-job/${sparkJobId}/explain`;

    let wasAborted = false;

    fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        Accept: 'text/event-stream',
      },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(text || `HTTP ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split('\n\n');
          buffer = chunks.pop() ?? '';

          for (const chunk of chunks) {
            const trimmed = chunk.trim();
            if (!trimmed) continue;

            if (trimmed === 'data: [DONE]') {
              return;
            }

            if (trimmed.startsWith('data: ')) {
              try {
                const event: SSEEvent = JSON.parse(trimmed.slice(6));
                processEvent(event);
              } catch {
                // Ignore malformed JSON
              }
            }
          }
        }
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') {
          wasAborted = true;
          return;
        }
        setError(err.message || 'Failed to generate explanation');
      })
      .finally(() => {
        setIsStreaming(false);
        if (!wasAborted) setIsComplete(true);
      });

    function processEvent(event: SSEEvent) {
      switch (event.type) {
        case 'tool-input-start':
          setToolCalls((prev) => [
            ...prev,
            {
              toolCallId: event.toolCallId as string,
              toolName: event.toolName as string,
              status: 'calling',
            },
          ]);
          break;

        case 'tool-output-available':
          setToolCalls((prev) =>
            prev.map((tc) =>
              tc.toolCallId === event.toolCallId
                ? { ...tc, status: 'done' as const }
                : tc
            )
          );
          break;

        case 'text-delta':
          setMarkdown((prev) => prev + (event.delta as string));
          break;

        case 'error':
          setError((event.errorText as string) || 'An error occurred');
          break;
      }
    }
  }, [
    isStreaming,
    reset,
    clientApiBaseUrl,
    currentProjectId,
    workspaceId,
    sparkJobId,
    authToken,
  ]);

  // Abort on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return {
    markdown,
    isStreaming,
    isComplete,
    error,
    toolCalls,
    explain,
    reset,
  };
}
